import { Mark, mergeAttributes } from '@tiptap/core';
import { swallowAnchorClicks } from './editor_anchor_clicks';

/**
 * A [[wikilink]] token, emitted by the server's Json render
 * (Objects/Text/Wiki/Render/Json/Ifixit/Wikilink.php) and re-emitted as
 * wikitext by to_wiki.ts. The marked text is the link's display text.
 *
 * A null target is the bare [[Title]] form: the text itself names the
 * linked page, so editing the text retargets the link. A non-null target
 * pins the page ([[Title|display text]]) and the text is free to change.
 */
export const WikiLink = Mark.create({
   name: 'wikiLink',
   inclusive: false,

   addAttributes() {
      return {
         target: {
            default: null,
            parseHTML: (element: HTMLElement) => element.dataset.target ?? null,
            renderHTML: attributes =>
               attributes.target === null ? {} : { 'data-target': attributes.target },
         },
         tag: {
            default: null,
            parseHTML: (element: HTMLElement) => element.dataset.tag ?? null,
            renderHTML: attributes =>
               attributes.tag === null ? {} : { 'data-tag': attributes.tag },
         },
         newWindow: {
            default: false,
            parseHTML: (element: HTMLElement) => element.dataset.newWindow === 'true',
            renderHTML: attributes => (attributes.newWindow ? { 'data-new-window': 'true' } : {}),
         },
         // Where the wiki page lives, for display only — the serialized
         // token carries the title, not the URL.
         href: {
            default: null,
            parseHTML: (element: HTMLElement) => element.getAttribute('href'),
            renderHTML: attributes => (attributes.href === null ? {} : { href: attributes.href }),
         },
      };
   },

   parseHTML() {
      return [{ tag: 'a[data-wikilink]' }];
   },

   renderHTML({ HTMLAttributes }) {
      return ['a', mergeAttributes({ 'data-wikilink': '' }, HTMLAttributes), 0];
   },

   addProseMirrorPlugins() {
      return [swallowAnchorClicks('wikiLinkClick', 'a[data-wikilink]')];
   },
});
